import { DataTexture, LinearFilter, RGBAFormat, SRGBColorSpace, UnsignedByteType } from 'three'

const TERRAIN_TEXTURE_SIZE = 512
const HASH_SCALE = 43758.5453
const TRACK_RADIUS = 0.46

const GREEN1 = [0.04, 0.22, 0.02] as const
const GREEN2 = [0.1, 0.4, 0.04] as const
const GREEN3 = [0.06, 0.3, 0.03] as const
const GREEN4 = [0.14, 0.42, 0.05] as const

const BROWN1 = [0.36, 0.16, 0.05] as const
const BROWN2 = [0.28, 0.12, 0.04] as const
const BROWN3 = [0.42, 0.2, 0.07] as const

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function linearToSrgb(value: number): number {
  const clamped = clamp01(value)
  if (clamped <= 0.0031308) return clamped * 12.92
  return 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055
}

function fract(value: number): number {
  return value - Math.floor(value)
}

function mix(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1
  const t = clamp01((x - edge0) / (edge1 - edge0))
  return t * t * (3 - 2 * t)
}

function hash(x: number, y: number): number {
  return fract(Math.sin(x * 127.1 + y * 311.7) * HASH_SCALE)
}

function noise(x: number, y: number): number {
  const ix = Math.floor(x)
  const iy = Math.floor(y)
  const fx = fract(x)
  const fy = fract(y)
  const ux = fx * fx * (3 - 2 * fx)
  const uy = fy * fy * (3 - 2 * fy)

  const a = hash(ix, iy)
  const b = hash(ix + 1, iy)
  const c = hash(ix, iy + 1)
  const d = hash(ix + 1, iy + 1)

  return mix(mix(a, b, ux), mix(c, d, ux), uy)
}

function fbm(x: number, y: number): number {
  let value = 0
  let amplitude = 0.5
  let frequency = 1

  for (let i = 0; i < 5; i++) {
    value += noise(x * frequency, y * frequency) * amplitude
    amplitude *= 0.5
    frequency *= 2.1
  }

  return value
}

function mixColor(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): readonly [number, number, number] {
  return [mix(a[0], b[0], t), mix(a[1], b[1], t), mix(a[2], b[2], t)]
}

interface TerrainNoiseSample {
  color: readonly [number, number, number]
  dirtMix: number
  denseNoise: number
}

export interface TerrainSurfaceBake {
  groundTexture: DataTexture
  maskTexture: DataTexture
  trackDist: Float32Array
  textureSize: number
}

function sampleTerrainNoise(worldX: number, worldZ: number, seed: number): TerrainNoiseSample {
  const n = fbm(worldX * 0.4 + seed, worldZ * 0.4 + seed)
  const n2 = fbm(worldX * 1.2 + seed + 3.7, worldZ * 1.2 + seed + 3.7)
  const n3 = fbm(worldX * 0.7 + seed + 9.1, worldZ * 0.7 + seed + 9.1)

  const grassMix = smoothstep(0.3, 0.7, n2)
  const grassColor = mixColor(mixColor(GREEN1, GREEN2, n2), mixColor(GREEN3, GREEN4, n3), grassMix)
  const dirtColor = mixColor(mixColor(BROWN1, BROWN2, n2), BROWN3, n3 * 0.5)
  const dirtMix = smoothstep(0.55, 0.75, n)

  return {
    color: mixColor(grassColor, dirtColor, dirtMix),
    dirtMix,
    denseNoise: n2,
  }
}

export function sampleTerrainGroundColor(
  worldX: number,
  worldZ: number,
  seed: number,
): readonly [number, number, number] {
  return sampleTerrainNoise(worldX, worldZ, seed).color
}

export function bakeTerrainSurface(
  rawPoints: number[],
  side: number,
  centerOffset: number,
  seed: number,
  radiusFactor = 1,
  textureSize = TERRAIN_TEXTURE_SIZE,
): TerrainSurfaceBake {
  const groundData = new Uint8Array(textureSize * textureSize * 4)
  const maskData = new Uint8Array(textureSize * textureSize * 4)
  const trackDist = new Float32Array(textureSize * textureSize)
  const texelSize = side / textureSize
  const levelRadius = side / 2
  const pointCount = rawPoints.length / 2
  const grassRadius2 = TRACK_RADIUS * TRACK_RADIUS

  for (let row = 0; row < textureSize; row++) {
    for (let col = 0; col < textureSize; col++) {
      const worldX = (col + 0.5) * texelSize - side / 2 + centerOffset
      const worldZ = side / 2 - (row + 0.5) * texelSize
      const pixel = row * textureSize + col

      let minDist2 = Infinity
      for (let i = 0; i < pointCount; i++) {
        const dx = worldX - rawPoints[i * 2]
        const dz = worldZ - rawPoints[i * 2 + 1]
        const d2 = dx * dx + dz * dz
        if (d2 < minDist2) minDist2 = d2
        if (minDist2 < grassRadius2) break
      }

      const outsideTrack = minDist2 < grassRadius2 ? 0 : 1
      const distToCenter = Math.hypot(worldX - centerOffset, worldZ)
      const radialWeight =
        1 -
        smoothstep(levelRadius * radiusFactor * 0.3, levelRadius * radiusFactor * 0.9, distToCenter)
      const withinRadius =
        1 - smoothstep(levelRadius * radiusFactor * 0.5, levelRadius * radiusFactor, distToCenter)
      const noiseSample = sampleTerrainNoise(worldX, worldZ, seed)
      const isGreen = (1 - noiseSample.dirtMix) * outsideTrack * radialWeight
      const density = isGreen * (1 - smoothstep(0.2, 0.8, noiseSample.denseNoise))
      const isBrown = noiseSample.dirtMix * withinRadius
      const dataIndex = pixel * 4

      groundData[dataIndex] = Math.round(linearToSrgb(noiseSample.color[0]) * 255)
      groundData[dataIndex + 1] = Math.round(linearToSrgb(noiseSample.color[1]) * 255)
      groundData[dataIndex + 2] = Math.round(linearToSrgb(noiseSample.color[2]) * 255)
      groundData[dataIndex + 3] = Math.round(clamp01(density) * 255)

      maskData[dataIndex] = Math.round(clamp01(isGreen) * 255)
      maskData[dataIndex + 1] = Math.round(clamp01(density) * 255)
      maskData[dataIndex + 2] = Math.round(clamp01(isBrown) * 255)
      maskData[dataIndex + 3] = 255

      trackDist[pixel] = Math.sqrt(minDist2)
    }
  }

  const groundTexture = new DataTexture(
    groundData,
    textureSize,
    textureSize,
    RGBAFormat,
    UnsignedByteType,
  )
  groundTexture.colorSpace = SRGBColorSpace
  groundTexture.minFilter = LinearFilter
  groundTexture.magFilter = LinearFilter
  groundTexture.generateMipmaps = false
  groundTexture.needsUpdate = true

  const maskTexture = new DataTexture(
    maskData,
    textureSize,
    textureSize,
    RGBAFormat,
    UnsignedByteType,
  )
  maskTexture.minFilter = LinearFilter
  maskTexture.magFilter = LinearFilter
  maskTexture.generateMipmaps = false
  maskTexture.needsUpdate = true

  return {
    groundTexture,
    maskTexture,
    trackDist,
    textureSize,
  }
}
